import React from 'react';
import { NavLink } from 'react-router-dom';
import * as R from 'ramda';
import keycode from 'keycode';
import { SearchInput } from '@economic/gaudi';
import Agreement from './Agreement';
import { useCurrentUser } from '@economic/react-app';

import { t } from '../../../shared/components/l10n/l10n';

const VismaConnectAddAccountDialog = React.lazy(() =>
    import(
        /* webpackChunkName: "visma-connect-add-account-dialog" */ '../../../shared/components/modals/VismaConnectAddAccountDialog'
    )
);
const VismaConnectMissing2FaDialog = React.lazy(() =>
    import(
        /* webpackChunkName: "visma-connect-missing-2fa-dialog" */ '../../../shared/components/modals/VismaConnectMissing2FaDialog'
    )
);

const sortByAgreementName = R.sortBy(R.compose(R.toLower, R.prop('companyName')));

interface Props {
    agreements: {
        agreementNumber: string;
        companyName: string;
        userId: string;
        isDeveloper: boolean;
        isAdministrator: boolean;
        hasCompany2FaEnforced: boolean;
        clients: {
            agreementNumber: string;
            companyName: string;
            isDeveloper: boolean;
            hasCompany2FaEnforced: boolean;
        }[];
    }[];
    onChangeAgreement: Function;
    refetchAgreements: Function;
    onToggleAllowedChange: (toggleAllowed: boolean) => void;
}

export const AgreementSelector = React.forwardRef(
    ({ agreements, onChangeAgreement, onToggleAllowedChange, refetchAgreements }: Props, ref: React.Ref<HTMLDivElement>) => {
        const [searchValue, setSearchValue] = React.useState('');
        const [showAddAccount, setShowAddAccount] = React.useState(false);
        const [showMissing2Fa, setShowMissing2Fa] = React.useState(false);
        const user = useCurrentUser();

        const onAddAccountCancelled = () => onToggleAllowedChange(true);
        const onAddAccountSuccess = () => {
            refetchAgreements();
            onToggleAllowedChange(true);
        };
        const onMissing2FaOk = () => {
            onToggleAllowedChange(true);
            setShowMissing2Fa(false);
        };

        const changeAgreement = agreement => {
            if (agreement.hasCompany2FaEnforced && user.isLoggedInWithVismaConnect && !user.isLoggedInWith2Fa) {
                onToggleAllowedChange(false);
                setShowMissing2Fa(true);
                return;
            }

            onChangeAgreement(agreement);
        };

        const handleAddAccount = () => {
            if (showAddAccount) {
                return;
            }

            onToggleAllowedChange(false);
            setShowAddAccount(true);
        };

        const handleSearch = event => {
            if (event.keyCode !== keycode(',')) {
                setSearchValue(event.target.value);
            }
        };

        const filterBySearchValue = agreement => {
            if (!searchValue || !searchValue.length) {
                return true;
            }
            var matchesAgreementNumber = String(agreement.agreementNumber).indexOf(searchValue) > -1;
            var matchesCompanyName = agreement.companyName.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;

            return matchesAgreementNumber || matchesCompanyName;
        };

        const filterAgreement = agreement => {
            return agreement.statuses.length === 0 && (!!agreement.clients || filterBySearchValue(agreement));
        };

        const filterClientAgreement = agreement => {
            if (agreement.statuses.length === 0 && (agreement.favorite || searchValue.length >= 3)) {
                return filterBySearchValue(agreement);
            }

            return false;
        };

        return (
            <div id="agreement_selector" className="bg-purple-dark flex flex-col absolute w-full" ref={ref}>
                <div id="agreement_selector__search" className="px-1 mt-1 w-full text-white">
                    <SearchInput
                        autoFocus
                        onChange={handleSearch}
                        placeholder={t`AGREEMENT_SELECTOR_SEARCH_PLACEHOLDER`}
                        value={searchValue}
                    />
                </div>
                <div id="agreement_selector__agreements" className="block w-full mb-auto overflow-y-auto p-1">
                    {sortByAgreementName(agreements)
                        .filter(filterAgreement)
                        .map(agreement =>
                            agreement.clients ? (
                                <div className="agreement_details__administrator" key={agreement.agreementNumber}>
                                    {filterBySearchValue(agreement) && (
                                        <Agreement
                                            name={agreement.companyName}
                                            number={agreement.agreementNumber}
                                            isAdministrator={agreement.isAdministrator}
                                            isDeveloper={agreement.isDeveloper}
                                            userId={agreement.userId}
                                            enforce2Fa={
                                                agreement.hasCompany2FaEnforced &&
                                                user.isLoggedInWithVismaConnect &&
                                                !user.isLoggedInWith2Fa
                                            }
                                            onClick={() => changeAgreement(agreement)}
                                        />
                                    )}
                                    <div className="agreement_details__administrator__clients">
                                        {sortByAgreementName(agreement.clients)
                                            .filter(filterClientAgreement)
                                            .map(client => (
                                                <Agreement
                                                    key={client.agreementNumber}
                                                    isDeveloper={client.isDeveloper}
                                                    name={client.companyName}
                                                    userId={agreement.userId}
                                                    number={client.agreementNumber}
                                                    enforce2Fa={
                                                        client.hasCompany2FaEnforced &&
                                                        user.isLoggedInWithVismaConnect &&
                                                        !user.isLoggedInWith2Fa
                                                    }
                                                    onClick={() => changeAgreement(client)}
                                                />
                                            ))}
                                    </div>
                                </div>
                            ) : (
                                <Agreement
                                    key={agreement.agreementNumber}
                                    name={agreement.companyName}
                                    number={agreement.agreementNumber}
                                    userId={agreement.userId}
                                    isAdministrator={agreement.isAdministrator}
                                    isDeveloper={agreement.isDeveloper}
                                    enforce2Fa={
                                        agreement.hasCompany2FaEnforced &&
                                        user.isLoggedInWithVismaConnect &&
                                        !user.isLoggedInWith2Fa
                                    }
                                    onClick={() => changeAgreement(agreement)}
                                />
                            )
                        )}
                </div>

                {user.vismaId && (
                    <button
                        id="agreement_selector__add_account"
                        className=" bg-purple-light  border-0 text-white cursor-pointer block flex-grow-0 flex-shrink-0 no-underline text-center duration-200 ease-in rounded text-sm h-10 leading-10 my-2 mx-auto hover:bg-purple-lightest"
                        onClick={handleAddAccount}>
                        {t`AGREEMENT_SELECTOR_ADD_ACCOUNT`}
                    </button>
                )}

                <NavLink
                    className="flex items-center justify-center h-10 hover:underline text-white border-t border-purple-light "
                    to="/logout">
                    {t`AGREEMENT_SELECTOR_LOG_OUT`}
                </NavLink>

                {showAddAccount && <VismaConnectAddAccountDialog onOk={onAddAccountSuccess} onCancel={onAddAccountCancelled} />}
                {showMissing2Fa && <VismaConnectMissing2FaDialog onOk={onMissing2FaOk} />}
            </div>
        );
    }
);
